import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";

import { AuthProvider } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import ProtectedRoute, { PublicRoute } from "./components/ProtectedRoute";
import ImpersonationBanner from "./components/ImpersonationBanner";

// Auth
import LoginPage               from "./pages/auth/LoginPage";
import ForceChangePasswordPage from "./pages/auth/ForceChangePasswordPage";
import SsoLandingPage          from "./pages/auth/SsoLandingPage";
import CompanionPage           from "./pages/CompanionPage";

// Super Admin
import SuperAdminLayout    from "./pages/superadmin/SuperAdminLayout";
import SuperAdminDashboard from "./pages/superadmin/SuperAdminDashboard";
import FacilitiesPage      from "./pages/superadmin/FacilitiesPage";
import UsersPage           from "./pages/superadmin/UsersPage";
import OrganisationsPage   from "./pages/superadmin/OrganisationsPage";
import BrandingSettingsPage from "./pages/superadmin/BrandingSettingsPage";

// Org Admin
import OrgAdminLayout  from "./pages/orgadmin/OrgAdminLayout";
import OrgOverviewPage from "./pages/orgadmin/OrgOverviewPage";
import OrgBranchesPage from "./pages/orgadmin/OrgBranchesPage";
import OrgReportsPage  from "./pages/orgadmin/OrgReportsPage";
import BranchManagePage from "./pages/orgadmin/BranchManagePage";
import BranchAdminLayout  from "./pages/orgadmin/BranchAdminLayout";
import BranchDashboardPage from "./pages/orgadmin/BranchDashboardPage";
import BranchZonesPage    from "./pages/orgadmin/BranchZonesPage";
import BranchStaffPage    from "./pages/orgadmin/BranchStaffPage";
import BranchSettingsPage from "./pages/orgadmin/BranchSettingsPage";
import BranchDisplayPage  from "./pages/orgadmin/BranchDisplayPage";
import BranchAudioPage    from "./pages/orgadmin/BranchAudioPage";
import BranchReportsPage  from "./pages/orgadmin/BranchReportsPage";
import OrgStaffPage       from "./pages/orgadmin/OrgStaffPage";
import { OrgAdminsPage, OrgActivityPage } from "./pages/orgadmin/OrgAdminsAndActivity";

// Facility Admin
import FacilityAdminLayout    from "./pages/facilityadmin/FacilityAdminLayout";
import FacilityAdminDashboard from "./pages/facilityadmin/FacilityAdminDashboard";
import ZonesPage              from "./pages/facilityadmin/ZonesPage";
import InsuranceRulesPage     from "./pages/facilityadmin/InsuranceRulesPage";
import QueueOverridePage      from "./pages/facilityadmin/QueueOverridePage";
import StaffPage              from "./pages/facilityadmin/StaffPage";
import SettingsPage           from "./pages/facilityadmin/SettingsPage";
import DisplayConfigPage      from "./pages/facilityadmin/DisplayConfigPage";
import FAQPage                from "./pages/facilityadmin/FAQPage";
import AudioPage              from "./pages/facilityadmin/AudioPage";
import ReportsPage            from "./pages/facilityadmin/ReportsPage";

// Staff
import StaffLayout    from "./pages/staff/StaffLayout";
import StaffQueuePage from "./pages/staff/StaffQueuePage";
import StaffStatsPage from "./pages/staff/StaffStatsPage";
import CompactQueuePage from "./pages/staff/CompactQueuePage";

// Public
import KioskPage        from "./pages/public/KioskPage";
import TrackingPage     from "./pages/public/TrackingPage";
import DisplayBoardPage from "./pages/public/DisplayBoardPage";
import BookingPage       from "./pages/public/BookingPage";

import "./styles/global.css";

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
      <AuthProvider>
        <ImpersonationBanner />
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: "#0F172A", color: "#F1F5F9",
              border: "1px solid rgba(37,99,235,0.3)",
              borderRadius: "10px", fontSize: "14px",
            },
            success: { iconTheme: { primary: "#22C55E", secondary: "#0F172A" } },
            error:   { iconTheme: { primary: "#EF4444", secondary: "#0F172A" } },
          }}
        />

        <Routes>
          {/* ── Auth ─────────────────────────────── */}
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/sso/:code" element={<SsoLandingPage />} />
          {/* Eliya Companion Mini Panel — see docs/adr/ADR-001-eliya-companion.md.
              Not wrapped in ProtectedRoute/PublicRoute: it manages its own
              logged-in/logged-out state on one page, since it's meant to be
              loaded inside a host app's iframe where a redirect to /login
              would navigate away from a page the host's CSP allowed in the
              first place. */}
          <Route path="/companion" element={<CompanionPage />} />
          <Route path="/change-password" element={<ProtectedRoute><ForceChangePasswordPage /></ProtectedRoute>} />

          {/* ── Super Admin ───────────────────────── */}
          <Route path="/super-admin" element={
            <ProtectedRoute allowedRoles={["super_admin"]}><SuperAdminLayout /></ProtectedRoute>
          }>
            <Route index               element={<SuperAdminDashboard />} />
            <Route path="organisations" element={<OrganisationsPage />} />
            <Route path="facilities"    element={<FacilitiesPage />} />
            <Route path="users"         element={<UsersPage />} />
            <Route path="branding"      element={<BrandingSettingsPage />} />
          </Route>

          {/* ── Org Admin ─────────────────────────── */}
          <Route path="/org" element={
            <ProtectedRoute allowedRoles={["org_admin", "super_admin"]}><OrgAdminLayout /></ProtectedRoute>
          }>
            <Route index             element={<OrgOverviewPage />} />
            <Route path="branches"   element={<OrgBranchesPage />} />
            <Route path="branches/:branchId" element={<BranchManagePage />} />
            <Route path="staff"          element={<OrgStaffPage />} />
            <Route path="reports"    element={<OrgReportsPage />} />
            <Route path="admins"     element={<OrgAdminsPage />} />
            <Route path="activity"   element={<OrgActivityPage />} />
          </Route>

          {/* ── Branch Admin (org admin managing a branch) ── */}
          <Route path="/org/branches/:branchId/manage" element={
            <ProtectedRoute allowedRoles={["org_admin", "super_admin"]}><BranchAdminLayout /></ProtectedRoute>
          }>
            <Route index           element={<BranchDashboardPage />} />
            <Route path="zones"    element={<BranchZonesPage />} />
            <Route path="staff"    element={<BranchStaffPage />} />
            <Route path="settings" element={<BranchSettingsPage />} />
            <Route path="display"  element={<BranchDisplayPage />} />
            <Route path="audio"    element={<BranchAudioPage />} />
            <Route path="reports"  element={<BranchReportsPage />} />
          </Route>

          {/* ── Facility Admin ────────────────────── */}
          <Route path="/admin" element={
            <ProtectedRoute allowedRoles={["facility_admin", "org_admin"]}><FacilityAdminLayout /></ProtectedRoute>
          }>
            <Route index           element={<FacilityAdminDashboard />} />
            <Route path="zones"    element={<ZonesPage />} />
            <Route path="insurance" element={<InsuranceRulesPage />} />
            <Route path="queue-override" element={<QueueOverridePage />} />
            <Route path="staff"    element={<StaffPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="display"  element={<DisplayConfigPage />} />
            <Route path="faq"      element={<FAQPage />} />
            <Route path="audio"    element={<AudioPage />} />
            <Route path="reports"  element={<ReportsPage />} />
            <Route path="activity" element={<OrgActivityPage />} />
          </Route>

          {/* ── Staff ─────────────────────────────── */}
          <Route path="/staff" element={
            <ProtectedRoute allowedRoles={["staff"]}><StaffLayout /></ProtectedRoute>
          }>
            <Route index        element={<StaffQueuePage />} />
            <Route path="stats" element={<StaffStatsPage />} />
          </Route>

          {/* Minimal embed widget for a third-party host's docked panel
              (Elyon HMS's QueueDockPanel) -- deliberately NOT nested under
              StaffLayout, so it renders with zero sidebar/nav chrome. */}
          <Route path="/staff/compact" element={
            <ProtectedRoute allowedRoles={["staff"]}><CompactQueuePage /></ProtectedRoute>
          } />

          {/* ── Public ────────────────────────────── */}
          <Route path="/kiosk/:slug"   element={<KioskPage />} />
          <Route path="/track/:token"  element={<TrackingPage />} />
          <Route path="/display/:slug" element={<DisplayBoardPage />} />
          <Route path="/book/:slug"    element={<BookingPage />} />

          {/* ── Root ──────────────────────────────── */}
          <Route path="/"  element={<Navigate to="/login" replace />} />
          <Route path="*"  element={<Navigate to="/login" replace />} />
        </Routes>
      </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
